import * as Sentry from "@sentry/react";
import React from "react";
import "@/i18n";
import ReactDOM from "react-dom/client";
import "@fontsource-variable/sora";
import "@/index.css";

import { SentryErrorBoundary } from "@/components/SentryErrorBoundary.tsx";
import { ThemeProvider } from "@/providers/ThemeProvider.tsx";
import { RouterProvider } from "react-router-dom";
import createRouter from "@/router";
import { CONFIG } from "@/consts/config";
import { loadDevMessages, loadErrorMessages } from "@apollo/client/dev";
import { setupChunkErrorHandler } from "@/hooks/useVersionCheck";
import { installTranslationGuard } from "@/lib/translationGuard";

const environment = CONFIG.sentryEnvironment ?? (import.meta.env.PROD ? "production" : "development");
const isProduction = environment === "production";

Sentry.init({
  dsn: "https://79c79ad2f84c3161a651492e0527241a@o4508052588724224.ingest.de.sentry.io/4508052595867728",
  environment,
  enabled: import.meta.env.PROD,

  // Release tracking for source maps
  release: import.meta.env.VITE_SENTRY_RELEASE,

  // Structured logs. console.warn/error go to Sentry Logs; console.log/info are
  // too high-volume to be worth the quota.
  enableLogs: true,

  integrations: [
    Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // Replays are for internal debugging only — nothing here is sensitive,
      // so don't mask. Defaults mask all text/inputs, making replays useless.
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
    }),
    Sentry.feedbackIntegration({
      autoInject: false,
      showBranding: false,
      colorScheme: "light",
    }),
  ],
  // Drop errors originating from browser extensions (e.g. fetch wrappers that
  // create their own unhandled rejections — see VENUS-4Y). Our code is not at
  // fault and these are not actionable.
  beforeSend(event) {
    const frames = event.exception?.values?.flatMap(
      (value) => value.stacktrace?.frames ?? [],
    );
    if (frames?.some((frame) => /^(chrome|moz|safari(-web)?)-extension:\/\//.test(frame.filename ?? ""))) {
      return null;
    }
    return event;
  },

  tracePropagationTargets: [CONFIG.httpLink, CONFIG.wsLink],
  tracesSampleRate: 1.0,
  // Only enable replays in production. replaysOnErrorSampleRate: 1 keeps every
  // session buffering in the background (rolling window, nothing uploaded) so
  // there's always pre-history to flush — on an error, or explicitly when the
  // feedback form opens (see SidebarLayout's onFormOpen). Without this, most
  // sessions have no replay running at all and a feedback-triggered replay
  // only captures the few seconds of the user filling out the form itself.
  // No random session sampling: the monthly replay quota is tiny, so uploads are
  // reserved for errors and feedback reports.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: isProduction ? 1 : 0,
});

if (!import.meta.env.PROD) {
  loadDevMessages();
  loadErrorMessages();
}

// Set up global error handler for chunk load failures
// This prevents 404 errors when old clients try to load new chunks after deployment
setupChunkErrorHandler();
installTranslationGuard();

const router = createRouter();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <SentryErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <RouterProvider router={router} />
      </ThemeProvider>
    </SentryErrorBoundary>
  </React.StrictMode>,
);
